import React, { useMemo, useState } from 'react';
import { ConfigurationCycleOption } from '../ConfigurationCycle/ConfigurationCycle';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { ButtonType } from '../../types/button-type';
import EmptyDataTable from '../EmptyDataTable/EmptyDataTable';

type ConfigurationCycleTagsProps = {
  disabled: boolean;
  listOptions: ConfigurationCycleOption[];
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectAllOptions: (data: ConfigurationCycleOption[]) => void;
  resetAllOptions: (data: ConfigurationCycleOption[]) => void;
  selectedValues: string[] | null;
};

// Same selection contract as ConfigurationCycleList (checkbox inputs, same
// onChange/selectAllOptions/resetAllOptions callbacks) but renders options as
// wrapping toggle tags instead of a vertical list, so long names just take
// the width they need instead of breaking a fixed column, and a search box
// helps narrow down a long option list.
const ConfigurationCycleTags: React.FC<ConfigurationCycleTagsProps> = ({
  disabled,
  listOptions,
  onChange,
  selectAllOptions,
  resetAllOptions,
  selectedValues,
}) => {
  const [search, setSearch] = useState('');

  const isChecked = (value: string) => !!selectedValues?.some((item) => item === value);

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return listOptions;
    return listOptions.filter((option) => option.name.toLowerCase().includes(query));
  }, [listOptions, search]);

  const disabledButton = listOptions.length === 0 || disabled;

  return (
    <div className="c-configuration-cycle-tags flex flex-auto flex-col">
      {listOptions.length === 0 && !disabled ? (
        <div className="relative flex flex-auto flex-col justify-center gap-y-2 overflow-hidden rounded-2xl border border-primary bg-white p-7 shadow-default dark:bg-boxdark">
          <EmptyDataTable className="border-none" text="NO OPTIONS AVAILABLE" />
        </div>
      ) : (
        <div
          className={`c-configuration-cycle-tags__list relative flex flex-auto flex-col gap-y-3 overflow-hidden rounded-2xl border bg-white p-5 shadow-default dark:bg-boxdark ${
            disabled
              ? 'pointer-events-none border-black before:absolute before:left-0 before:top-0 before:z-1 before:h-full before:w-full before:bg-red-500 before:content-[\'\']'
              : 'border-primary'
          }`}
        >
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search…"
            className="w-full rounded-lg border border-stroke bg-transparent px-3 py-1.5 text-sm text-black outline-none focus:border-primary dark:border-strokedark dark:text-white"
          />
          <div className="flex flex-wrap content-start gap-1.5 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <p className="w-full py-4 text-center text-sm text-body dark:text-bodydark">No matches</p>
            ) : (
              filteredOptions.map((option) => {
                const checked = isChecked(option.value);

                return (
                  <label
                    key={option.value}
                    htmlFor={`tag-${option.value}`}
                    title={option.name}
                    className={`flex max-w-[220px] cursor-pointer items-center gap-1.5 truncate rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                      checked
                        ? 'border-primary bg-primary text-white'
                        : 'border-stroke text-black hover:border-primary dark:border-strokedark dark:text-white'
                    }`}
                  >
                    <input
                      type="checkbox"
                      id={`tag-${option.value}`}
                      name={option.name}
                      value={option.value}
                      checked={checked}
                      onChange={onChange}
                      className="hidden"
                    />
                    {checked && (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="flex-none">
                        <path d="M2 5l2.2 2.2L8 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    <span className="truncate">{option.name}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
      <div className="c-configuration-cycle-tags__actions flex flex-row justify-center gap-x-4 px-6 pt-4">
        <InteractiveButton
          type={ButtonType.PrimaryInverse}
          text="None"
          onClick={() => resetAllOptions(listOptions)}
          disabled={disabledButton}
        />
        <InteractiveButton
          type={ButtonType.Primary}
          text="All"
          onClick={() => selectAllOptions(listOptions)}
          disabled={disabledButton}
        />
      </div>
    </div>
  );
};

export default ConfigurationCycleTags;
